import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ChatMessage } from '../lib/types';
import { copyAnswer } from '../lib/clipboard';
import { Sources } from './Sources';
import { AddKnowledge } from './AddKnowledge';

interface Props {
  message: ChatMessage;
  /** The question this answer replied to — carried into a correction. */
  question?: string;
  company?: string;
  division?: string;
}

export function MessageBubble({ message, question, company = 'de-graaf', division = 'Alle' }: Props) {
  const [copied, setCopied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const isUser = message.role === 'user';
  // Copying the rendered node rather than re-rendering the markdown a second
  // time: what lands on the clipboard is then exactly what is on screen.
  const bodyRef = useRef<HTMLDivElement>(null);

  async function copy() {
    try {
      await copyAnswer(bodyRef.current?.innerHTML, message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  if (isUser) {
    return (
      <div className="bubble bubble-user">
        <div className="bubble-body">{message.content}</div>
      </div>
    );
  }

  return (
    <div className={`bubble bubble-nina${message.error ? ' bubble-error' : ''}`}>
      <div className="bubble-head">
        <span className="nina-dot" aria-hidden>⬤</span>
        <span className="nina-name">Nina</span>
        {message.category && <span className="badge">{message.category}</span>}
        {!message.error && (
          <button className="copy-btn" onClick={copy} title="Kopiëren">
            {copied ? 'Gekopieerd ✓' : 'Kopiëren'}
          </button>
        )}
      </div>
      <div className="bubble-body markdown" ref={bodyRef}>
        {/* The bubble is created empty and fills as the stream arrives, so the
            typing dots belong inside it — a separate indicator bubble would sit
            under the answer looking like a second, stuck reply. */}
        {message.content
          ? <ReactMarkdown>{message.content}</ReactMarkdown>
          : <div className="typing"><span></span><span></span><span></span></div>}
      </div>
      {message.sources && <Sources sources={message.sources} />}

      {/* Correction lives on the answer, not in a settings screen: the moment
          someone notices a gap is the only moment they will reliably fix it. */}
      {!message.error && question && !added && (
        adding ? (
          <AddKnowledge
            question={question}
            company={company}
            division={division}
            onDone={(saved) => { setAdding(false); if (saved) setAdded(true); }}
          />
        ) : (
          <button className="correct-btn" onClick={() => setAdding(true)}>
            Klopt dit niet? Vul de kennisbank aan
          </button>
        )
      )}
      {added && <p className="ak-done">Toegevoegd aan de kennisbank ✓</p>}
    </div>
  );
}
